/* ============================================================
   hiba-living-bg.js — The Reprogramming "alive environment"
   Floating soft colour on white (multiply-blended radial blobs)
   + drifting particles. Constant gentle float; intensifies and
   turns audio-reactive while a track plays.

   Ported from the approved prototype (preview.html). Decoupled
   from the player: the player calls HibaLivingBG.setPlayIndex(i)
   and .attachAnalyser(node) — this file owns only the canvas.

   Mount: <canvas class="hr-bg" data-living-bg
                  data-palette="[[232,17,121],...]"></canvas>
   Respects prefers-reduced-motion (static gradient, no loop)
   and pauses when the tab is hidden.
   ============================================================ */
(function () {
  'use strict';

  var DPR = Math.max(1, window.devicePixelRatio || 1);
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var cv, ctx, W, H;
  var blobs = [];
  var parts = [];
  var raf = null;
  var playIdx = -1;
  var intensity = 0;
  var palette = [[247, 206, 224]];
  var BASE = '#FBF4F0';   // light bone base (set per-room via data-base); soft colour washes on top

  var analyser = null;
  var freq = null;
  var reactive = false;

  function resize() {
    if (!cv) return;
    W = cv.width = window.innerWidth * DPR;
    H = cv.height = window.innerHeight * DPR;
  }

  function setPalette(pal) {
    palette = (pal && pal.length) ? pal : palette;
    blobs = [];
    for (var i = 0; i < 6; i++) {
      var c = palette[i % palette.length];
      blobs.push({
        c: c,
        x: Math.random(), y: Math.random(),
        px: Math.random() * 6.28, py: Math.random() * 6.28,
        sx: 0.00010 + Math.random() * 0.00012,
        sy: 0.00010 + Math.random() * 0.00012,
        r: 0.5 + Math.random() * 0.4
      });
    }
  }

  function avg(a) {
    var s = 0;
    for (var i = 0; i < a.length; i++) s += a[i];
    return s / a.length;
  }

  /* Static, single-frame soft-wash fallback for reduced-motion / no-canvas */
  function paintStatic() {
    if (!ctx) return;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = BASE;
    ctx.fillRect(0, 0, W, H);
    blobs.forEach(function (b, i) {
      var x = W * (0.5 + (b.x - 0.5) * 0.5);
      var y = H * (0.5 + (b.y - 0.5) * 0.5);
      var R = Math.max(W, H) * b.r * 0.9;
      var g = ctx.createRadialGradient(x, y, 0, x, y, R);
      g.addColorStop(0, 'rgba(' + b.c[0] + ',' + b.c[1] + ',' + b.c[2] + ',0.5)');
      g.addColorStop(1, 'rgba(' + b.c[0] + ',' + b.c[1] + ',' + b.c[2] + ',0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, R, 0, 6.2832);
      ctx.fill();
    });
  }

  function draw(t) {
    var target;
    if (reactive && analyser) {
      analyser.getByteFrequencyData(freq);
      var e = avg(freq) / 255;
      target = playIdx >= 0 ? Math.min(1, 0.3 + e * 1.1) : 0;
    } else {
      target = playIdx >= 0 ? 0.5 + 0.2 * Math.sin(t * 0.0025) : 0;
    }
    intensity += (target - intensity) * 0.05;   // slow to settle = calmer
    var I = intensity;
    var amp = 0.12 + I * 0.06;   // gentle constant breathing; barely more on play
    var sp = 0.5 + I * 0.4;      // slow colour movement

    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = BASE;
    ctx.fillRect(0, 0, W, H);

    blobs.forEach(function (b, i) {
      var x = W * (0.5 + Math.sin(t * b.sx * sp + b.px) * amp + (b.x - 0.5) * 0.3);
      var y = H * (0.5 + Math.cos(t * b.sy * sp + b.py) * amp + (b.y - 0.5) * 0.3);
      var R = Math.max(W, H) * b.r * (0.95 + I * 0.25);   // soft, wide fields
      var a = 0.42 + I * 0.16;   // soft wash over bone; deepens slightly on play
      var g = ctx.createRadialGradient(x, y, 0, x, y, R);
      g.addColorStop(0, 'rgba(' + b.c[0] + ',' + b.c[1] + ',' + b.c[2] + ',' + a + ')');
      g.addColorStop(1, 'rgba(' + b.c[0] + ',' + b.c[1] + ',' + b.c[2] + ',0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, R, 0, 6.2832);
      ctx.fill();
    });

    if (playIdx >= 0 && Math.random() < I * 0.18) {
      var c = blobs[(Math.random() * blobs.length) | 0].c;
      parts.push({
        x: Math.random() * W, y: H + 10,
        vy: (0.2 + Math.random() * 0.5) * DPR,
        r: (1.2 + Math.random() * 2) * DPR,
        life: 1, c: c
      });
    }

    for (var i = parts.length - 1; i >= 0; i--) {
      var p = parts[i];
      p.y -= p.vy * (1 + I * 0.5);
      p.x += Math.sin(t * 0.0008 + p.y * 0.01) * 0.4;
      p.life -= 0.0035;
      if (p.life <= 0 || p.y < -20) { parts.splice(i, 1); continue; }
      var pr = Math.round(p.c[0] * 0.6), pg = Math.round(p.c[1] * 0.6), pb = Math.round(p.c[2] * 0.6);
      ctx.fillStyle = 'rgba(' + pr + ',' + pg + ',' + pb + ',' + (p.life * 0.18) + ')';
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, 6.28);
      ctx.fill();
    }

    raf = window.requestAnimationFrame(draw);
  }

  function start() {
    if (REDUCED) { paintStatic(); return; }
    if (!raf) raf = window.requestAnimationFrame(draw);
  }
  function stop() {
    if (raf) { window.cancelAnimationFrame(raf); raf = null; }
  }

  function init(canvas) {
    cv = canvas;
    ctx = cv.getContext('2d');
    if (!ctx) return;
    var pal;
    try { pal = JSON.parse(cv.getAttribute('data-palette') || '[]'); } catch (e) { pal = []; }
    BASE = cv.getAttribute('data-base') || BASE;
    resize();
    setPalette(pal);
    window.addEventListener('resize', function () { resize(); if (REDUCED) paintStatic(); });
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });
    start();
  }

  // Public API for the player module
  window.HibaLivingBG = {
    init: init,
    setPlayIndex: function (i) { playIdx = (typeof i === 'number') ? i : -1; if (!REDUCED) start(); },
    attachAnalyser: function (node) {
      analyser = node;
      if (analyser) { freq = new Uint8Array(analyser.frequencyBinCount); reactive = true; }
    },
    stop: stop,
    start: start
  };

  function boot() {
    var canvas = document.querySelector('canvas.hr-bg[data-living-bg]');
    if (canvas) init(canvas);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
